import { useQueries } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import { apiFetch } from '@/api/client';
import { Button, Card } from '@/components/PageHeading';
import type { GarmentRateCard, ItemUnit } from '@core/api/masters';
import { garmentRateCardPath } from '@core/api/queryPaths';
import { hangTag, tagSizes, type TagSize } from '@core/print/hangTag';
import { hangTagsPage } from '@core/print/printHtml';
import { can } from '@core/session/capability';
import type { Session } from '@core/session/types';

const ptToPx = (pt: number) => (pt * 96) / 72;

/**
 * The tags for garments just booked in — the core's `hangTagsPage`, the page the phone prints, drawn
 * in a frame and printed from it, so a tag reads the same from either client. Each carries its rate
 * only for a role that may see money (`ItemUnit:ViewFinancials`).
 *
 * <p>
 * <strong>One rate card per configuration, not per garment.</strong> Booking in prices the
 * configuration (`SetEventRateAsync` on the variant), and a garment just booked in has no card of
 * its own, so every garment of a configuration resolves to the same card — and an import of four
 * hundred garments asks a dozen times instead of four hundred.
 * </p>
 */
export function TagPrint({ session, units }: { session: Session; units: readonly ItemUnit[] }) {
  const maySeeRate = can(session, 'ItemUnit:ViewFinancials');
  const [size, setSize] = useState<TagSize>('40x60');
  const frame = useRef<HTMLIFrameElement>(null);

  const firstOfVariant = [...new Map(units.map((unit) => [unit.variantId, unit])).values()];
  const cards = useQueries({
    queries: firstOfVariant.map((unit) => ({
      queryKey: ['item-unit', unit.id, 'rate-card'],
      queryFn: () => apiFetch<GarmentRateCard>(garmentRateCardPath(unit.id)),
      enabled: maySeeRate,
    })),
  });
  const cardsReady = !maySeeRate || cards.every((card) => card.data || card.isError);
  const cardOf = new Map(
    firstOfVariant.map((unit, index) => [unit.variantId, cards[index]?.data ?? null]),
  );

  const page = hangTagsPage(
    units.map((unit) => hangTag(unit, maySeeRate ? (cardOf.get(unit.variantId) ?? null) : null)),
    size,
  );

  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <p className="me-auto mb-0 text-sm font-semibold text-gray-900">Tags</p>
        <div
          role="group"
          aria-label="Tag size"
          className="border-border-color flex overflow-hidden rounded-md border bg-white"
        >
          {(['40x60', '50x80'] as const).map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={option === size}
              onClick={() => setSize(option)}
              className={`cursor-pointer px-3 py-1.5 text-[13px] whitespace-nowrap ${
                option === size
                  ? 'bg-primary font-semibold text-white'
                  : 'hover:bg-light text-gray-900'
              }`}
            >
              {tagSizes[option].label}
            </button>
          ))}
        </div>
        <Button
          variant="primary"
          icon="icon icon-printer"
          shortcut="print"
          disabled={!cardsReady}
          onClick={() => frame.current?.contentWindow?.print()}
        >
          {cardsReady
            ? `Print ${units.length} tag${units.length === 1 ? '' : 's'}`
            : 'Reading the rates…'}
        </Button>
      </div>
      {/* The preview is the print: the frame holds the core's tag page at its true size. */}
      <div className="bg-light border-border-color max-h-[460px] overflow-auto rounded-md border p-3">
        <iframe
          ref={frame}
          title={`${units.length} hang tags`}
          srcDoc={page.html}
          className="mx-auto block border-0 bg-white shadow-sm"
          style={{
            width: ptToPx(page.widthPt) + 16,
            height: ptToPx(page.heightPt) * Math.min(units.length, 3),
          }}
        />
      </div>
    </Card>
  );
}
